import React, { useEffect, useState } from 'react';
import { audioManager } from '../../utils/audioManager';

/**
 * AudioPlayer 组件 - 背景音乐播放器
 *
 * 功能：
 * 1. 用户首次点击黑色背景任意位置 → 自动播放音乐
 * 2. 右上角半透明播放器 UI，显示音乐信息和暂停/播放按钮
 * 3. 跨页面共享播放状态（Login 和 SignUp 共享）
 * 4. 音量 50%，带淡入效果
 */
const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  // 初始化：从全局管理器获取状态
  useEffect(() => {
    // 初始化音频管理器
    audioManager.init();

    // 同步当前播放状态
    const currentPlaying = audioManager.isPlaying();
    const currentStarted = audioManager.hasStarted();
    setIsPlaying(currentPlaying);
    setHasStarted(currentStarted);

    // 监听状态变化
    const handleStateChange = () => {
      setIsPlaying(audioManager.isPlaying());
      setHasStarted(audioManager.hasStarted());
    };

    // 注册监听器
    audioManager.addListener(handleStateChange);

    // 清理函数：移除监听器（但不暂停音频，因为可能在其他页面还在播放）
    return () => {
      audioManager.removeListener(handleStateChange);
    };
  }, []);

  // 处理背景点击（首次触发播放）
  useEffect(() => {
    // 如果已经启动过，不再监听
    if (hasStarted || audioManager.hasStarted()) return;

    const handleBackgroundClick = e => {
      // 排除表单元素和按钮
      const tagName = e.target.tagName;
      const isFormElement =
        tagName === 'INPUT' ||
        tagName === 'BUTTON' ||
        tagName === 'SELECT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'A' ||
        e.target.closest('form') ||
        e.target.closest('button') ||
        e.target.closest('a');

      // 如果点击的是播放器本身，不触发
      if (e.target.closest('[data-audio-player]')) {
        return;
      }

      // 再次检查状态（防止重复触发）
      if (!isFormElement && !audioManager.hasStarted()) {
        // 立即移除事件监听器，防止重复触发
        document.removeEventListener('click', handleBackgroundClick);

        audioManager
          .play()
          .then(() => {
            setHasStarted(true);
            setIsPlaying(true);
          })
          .catch(error => {
            console.error('播放失败:', error);
          });
      }
    };

    // 添加事件监听器
    document.addEventListener('click', handleBackgroundClick);

    return () => {
      document.removeEventListener('click', handleBackgroundClick);
    };
  }, [hasStarted]);

  // 切换播放/暂停
  const togglePlay = e => {
    e.stopPropagation(); // 阻止事件冒泡，避免触发背景点击

    audioManager
      .toggle()
      .then(() => {
        setIsPlaying(audioManager.isPlaying());
        setHasStarted(audioManager.hasStarted());
      })
      .catch(error => {
        console.error('播放失败:', error);
      });
  };

  return (
    <div
      data-audio-player
      className='fixed top-4 right-6 z-50 pointer-events-auto flex items-center gap-3'
      style={{
        opacity: hasStarted ? 0.9 : 0.7, // 提高可见度：已启动 0.9，未启动 0.7
        transition: 'opacity 0.3s ease',
      }}
    >
      {/* 音乐信息 - 滚动显示（无背景框，低透明度细体） */}
      <div
        className='flex-1 overflow-hidden relative'
        style={{ height: '20px', width: '90px' }}
      >
        <div
          className='text-white whitespace-nowrap absolute font-light'
          style={{
            fontSize: '14px',
            opacity: 0.4, // 降低文字透明度
            animation: isPlaying ? 'scroll-text 15s linear infinite' : 'none',
            left: '100%',
          }}
        >
          &quot;Dawn&quot; by Sappheiros
        </div>
      </div>

      {/* 暂停/播放按钮（无背景框） */}
      <button
        onClick={togglePlay}
        className='flex-shrink-0 text-white hover:text-white transition-colors focus:outline-none'
        style={{
          filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))',
        }}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          // 暂停图标 ⏸️
          <svg
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='currentColor'
            xmlns='http://www.w3.org/2000/svg'
          >
            <rect x='6' y='4' width='4' height='16' />
            <rect x='14' y='4' width='4' height='16' />
          </svg>
        ) : (
          // 播放图标 ▶️
          <svg
            width='18'
            height='18'
            viewBox='0 0 24 24'
            fill='currentColor'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d='M8 5v14l11-7z' />
          </svg>
        )}
      </button>

      {/* CSS 动画 - 文字滚动 */}
      <style>{`
        @keyframes scroll-text {
          0% {
            left: 100%;
          }
          100% {
            left: -100%;
          }
        }
      `}</style>
    </div>
  );
};

export default AudioPlayer;
